// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

// URL de base de l'API Flask (à adapter quand le backend sera prêt)
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);   // { id, nom, prenom, email, role }
  const [token, setToken]     = useState(null);
  const [loading, setLoading] = useState(true);   // chargement initial

  // ── Restaurer la session depuis localStorage au démarrage ──
  useEffect(() => {
    const savedToken = localStorage.getItem('bdk_token');
    const savedUser  = localStorage.getItem('bdk_user');
    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch { clearSession(); }
    }
    setLoading(false);
  }, []);

  function saveSession(tokenValue, userData) {
    localStorage.setItem('bdk_token', tokenValue);
    localStorage.setItem('bdk_user',  JSON.stringify(userData));
    setToken(tokenValue);
    setUser(userData);
  }

  function clearSession() {
    localStorage.removeItem('bdk_token');
    localStorage.removeItem('bdk_user');
    setToken(null);
    setUser(null);
  }

  // ── LOGIN ──
  async function login(email, password) {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Email ou mot de passe incorrect.');
    saveSession(data.access_token, data.user);
    return data.user;
  }

  // ── REGISTER ──
  async function register(nom, prenom, email, password) {
    const res = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nom, prenom, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Erreur lors de l\'inscription.');
    saveSession(data.access_token, data.user);
    return data.user;
  }

  // ── LOGOUT ──
  const logout = useCallback(() => {
    clearSession();
  }, []);

  // ── Appel API authentifié (helper) ──
  async function authFetch(endpoint, options = {}) {
    const res = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    if (res.status === 401) { logout(); throw new Error('Session expirée.'); }
    return res;
  }

  const isLoggedIn = !!user && !!token;
  const isAdmin    = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      isLoggedIn, isAdmin,
      login, register, logout, authFetch,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }